import { OmniElement, repeat, html, css } from 'omni-ui';
import { produce } from 'immer';
import CentralizedDataAPI from '../../services/centralized-data-api.js';

/** callback for sorting segmentCards */
const segmentsCompareFn = (a, b) => {
  const criteria = {
    pinned: -(a.isPinned - b.isPinned),
    alpha: a.title.localeCompare(b.title),
  };

  return criteria.pinned || criteria.alpha;
};

export class CampaignAudienceAddSegments extends OmniElement {
  static get styles() {
    return [
      ...super.styles,
      css`
        :host {
          z-index: 2;
        }
        :host,
        [drag-able] {
          max-width: 25.7rem;
        }
        omni-toolbar h3 {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 19.79rem;
        }
        omni-tile::part(body-slot) {
          padding: 1.14rem 0.71rem 0 1.71rem;
        }
        .search-container {
          padding-bottom: 0.57rem;
          margin-right: 1rem;
        }
        .omni-search {
          width: 21.21rem;
          height: 2.57rem;
        }
        .segments-list {
          max-height: 21.42rem;
          overflow-y: scroll;
          display: flex;
          flex-direction: column;
          flex-wrap: nowrap;
          align-content: center;
          justify-content: flex-start;
          align-items: stretch;
          gap: 0.57rem;
          padding-right: 0.71rem;
          height: 31.714rem;
        }
        div.segments-list::-webkit-scrollbar {
          width: 0.29rem;
          height: 0;
        }
        div.segments-list::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 0.29rem;
        }

        div.segments-list::-webkit-scrollbar-track {
          background-color: transparent;
        }
        .segment-card {
          border-radius: 0.71429rem;
          border: thin solid var(--color-border, #edf0f5);
          background: var(--color-core-light, #fff);
          transition: box-shadow 0.3s ease-in-out;
        }
        .segment-card:hover {
          box-shadow: 0px 24px 31px -6px rgba(var(--rgb-core-dark), 0.35);
        }
        .content {
          display: flex;
          flex-flow: row nowrap;
          gap: 1.14rem;
          padding: 1.14rem;
        }
        .segment-card-left {
          min-width: 2.857rem;
        }
        .omni img.image {
          width: 2.857rem;
          height: 2.857rem;
          object-fit: cover;
          object-position: center;
          border-radius: 50%;
        }
        .img-placeholder {
          width: 100%;
          height: 100%;
          font-size: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .img-placeholder omni-icon {
          --color-icon-lines: var(--color-core-medium);
        }
        .segment-card-middle {
          flex: 1 1 100%;
          display: flex;
          flex-direction: column;
          justify-content: left;
        }
        .segment-card-text {
          margin-bottom: 0.25rem;
          font-weight: 600;
          font-size: 0.86rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 12.79rem;
        }
        .segment-details-container {
          display: flex;
          flex-direction: column;
        }
        .segment-details {
          color: var(--color-almost-black);
          opacity: 0.5;
          font-size: var(--size-7);
          letter-spacing: 0.02rem;
          font-weight: normal;
          display: flex;
          justify-content: start;
        }
        .detail-button {
          padding: 0.25rem !important;
          width: fit-content;
        }
        .card-checkbox:not([checked]) {
          visibility: hidden;
        }
        .segment-card:hover .card-checkbox {
          visibility: visible;
        }
      `,
    ];
  }

  static get properties() {
    return {
      audience: { type: Object },
      stage: { type: Object },
      groups: { type: Object },
      expanded: { type: Array },
      loadingCustomSegments: { type: Boolean },
      customSegments: { type: Array },
    };
  }

  constructor() {
    super();
    this.loadingCustomSegments = false;
    this.pinnedSegments = [];
    this.customSegments = [];
    this.expanded = [];
    this.groups = {};
  }

  /**
   * @description creates a new instance of the CentralizedDataAPI and adds an event listener to the document
   */
  connectedCallback() {
    super.connectedCallback();
    this.objectsAPI = new CentralizedDataAPI(this.token, this.env);
    document.addEventListener('mousedown', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('mousedown', this.#handleDocumentClick);
    super.disconnectedCallback();
  }

  firstUpdated() {
    this.#setPinnedSegments();
    this.#getCustomSegments();
  }

  updated(changedProperties) {
    if (changedProperties.has('audience')) {
      this.#setPinnedSegments();
    }
  }

  /**
   *
   * @param {*} event passed by the event listener "click"
   * @description method that handles the closing of the component
   * @returns out of the method
   */
  #handleDocumentClick = event => {
    const popup = this.shadowRoot.querySelector('omni-tile');
    if (event.composedPath().includes(popup)) return;
    this.#closeAddSegmentsCard();
  };

  #closeAddSegmentsCard = () => {
    this.dispatchNewEvent('close-add-segments');
  };

  /**
   * @description method to get the pinned custom segments from the audience object
   */
  #setPinnedSegments = () => {
    const groupName =
      this.audience.stagePercentages[this.percentageIndex]?.group;
    this.pinnedSegments = groupName
      ? this.groups[groupName].customSegments
      : this.audience.stagePercentages[this.percentageIndex].customSegments;
  };

  /**
   *
   * @param {String} search
   * @description method to get custom segments from the AE API
   */
  #getCustomSegments = async (search = '') => {
    let customSegments = [];
    try {
      this.loadingCustomSegments = true;

      const urlParams = new URLSearchParams({
        length: '250',
        search,
        guid_client: this.context.clientId,
      });
      const { data: results = [] } = await this.objectsAPI.request(
        '2e24b2c2-f47a-4401-b73c-e00498514c74',
        urlParams,
      );
      // return an array of objects with the required properties
      customSegments = results.map(
        ({
          name: title,
          uuid_object: uuid,
          image_url: img,
          data_points: audienceDataPoints,
        }) => {
          const dataPoints = Array.isArray(audienceDataPoints)
            ? audienceDataPoints.reduce((agg, dp) => ({ ...agg, ...dp }))
            : audienceDataPoints;
          const details = {
            'Target Size': dataPoints?.id_count,
            'Total Size': dataPoints?.pop_id_count,
            Country: dataPoints?.data_environment_name,
            Project: dataPoints?.project_name,
          };
          return {
            img,
            title,
            uuid,
            details,
            isPinned: false,
          };
        },
      );
      // We don't want to provide the user with a segment that is being used as an Audience
      customSegments = customSegments.filter(
        segment => !this.selectedAEObject.some(uuid => uuid === segment.uuid),
      );
      // If object in pinnedSegments doesn't exist in customSegments add the object to customSegments, if it does, update isPinned to true
      const segmentMap = new Map(
        customSegments.map(segment => [segment.uuid, segment]),
      );
      this.pinnedSegments.forEach(pinnedSegment => {
        const segment = segmentMap.get(pinnedSegment.uuid);
        if (segment) {
          segment.isPinned = true;
        } else {
          customSegments.push(pinnedSegment);
        }
      });
    } catch (error) {
      this.loadingCustomSegments = false;
      console.error('Error getting AE objects:', error);
    }
    this.loadingCustomSegments = false;
    this.customSegments = customSegments.sort(segmentsCompareFn);
  };

  /**
   * @param {*} event passed by the event listener "search-update"
   * @description  method to get custom segments from the AE API with the search value
   *
   */
  #updateSearch(e) {
    const search = e?.detail?.value;
    this.#getCustomSegments(search);
  }

  /**
   *
   * @param {Number} idx
   * @description method to toggle the isPinned property of a custom segment, add the segment to the pinnedSegments array and dispatch a new event to update the custom segments in the audience object
   * @event update-custom-segments - event to update the custom segments in the audience object. Listened by <campaign-audience-viz>
   */
  #toggleCheck = async idx => {
    const groupName =
      this.audience.stagePercentages[this.percentageIndex]?.group;

    this.customSegments = produce(this.customSegments, draft => {
      draft[idx].isPinned = !draft[idx].isPinned;
      draft.sort(segmentsCompareFn);
    });

    const pinnedSegments = this.customSegments.filter(
      segment => segment.isPinned,
    );

    this.dispatchNewEvent('update-custom-segments', {
      detail: {
        pinnedSegments,
        percentageIndex: this.percentageIndex,
        audienceIndex: this.audienceIndex,
        groupName,
      },
    });
  };

  /**
   *
   * @param {Object} segment
   * @param {Number} idx
   * @returns {HTMLTemplateElement} template for the segment card details
   */
  #renderSegmentDetails = (segment, idx) => {
    const titles = ['Target Size', 'Total Size', 'Country', 'Project'];
    if (this.expanded.includes(idx)) {
      return html`
        <div class="segment-details-container">
          ${repeat(
            titles,
            title => `${title}`,
            title => html`
              <div class="segment-details">
                ${title}: ${segment.details[title]?.toLocaleString('en-US')}
              </div>
            `,
          )}
          <button
            class="button is-text is-small detail-button"
            @click=${() => {
              this.expanded = produce(this.expanded, draft => {
                draft.splice(draft.indexOf(idx), 1);
              });
            }}
          >
            Collapse Details
          </button>
        </div>
      `;
    }
    return html`
      <div class="segment-details">
        ${`Target size : ${segment.details['Target Size']?.toLocaleString(
          'en-US',
        )}`}
      </div>
      <button
        class="button is-text is-small detail-button"
        @click=${() => {
          this.expanded = produce(this.expanded, draft => {
            draft.push(idx);
          });
        }}
      >
        View Details
      </button>
    `;
  };

  /**
   *
   * @param {Object} segment
   * @param {Number} idx
   * @returns {HTMLTemplateElement} template for the segment card
   */
  #renderSegmentCard = (segment, idx) => html`
    <div class="segment-card">
      <div class="content">
        <div class="segment-card-left">
          ${segment.img
            ? html`
                <img class="image" loading="lazy" src=${segment.img} alt="" />
              `
            : html`
                <div class="img-placeholder">
                  <omni-icon icon-id="omni:app:contentAutomation"></omni-icon>
                </div>
              `}
        </div>
        <div class="segment-card-middle">
          <div class="segment-card-text ">${segment.title}</div>
          ${this.#renderSegmentDetails(segment, idx)}
        </div>
        <div class="segment-card-right">
          <omni-checkbox
            class="card-checkbox"
            .checked=${segment?.isPinned}
            @change=${() => {
              this.#toggleCheck(idx);
            }}
          ></omni-checkbox>
        </div>
      </div>
    </div>
  `;

  render() {
    const groupName =
      this.audience.stagePercentages[this.percentageIndex]?.group;
    return html`
      <omni-style>
        <omni-tile drag-able>
          <omni-toolbar slot="header">
            <h3 slot="start" class="title is-4 has-text-weight-normal">
              Custom Segments:
              ${groupName || `${this.audience.name} - ${this.stage.name}`}
            </h3>
            <button
              slot="end"
              class="button is-text is-shadowless"
              @click=${this.#closeAddSegmentsCard}
            >
              <omni-icon
                class="is-size-1"
                icon-id="omni:interactive:close"
              ></omni-icon>
            </button>
          </omni-toolbar>

          <div class="search-container">
            <omni-search
              isopen
              @search-update=${e => {
                this.#updateSearch(e);
              }}
              slot="end"
              ph="Search Custom Segments"
            ></omni-search>
          </div>

          <div class="segments-list">
            ${this.loadingCustomSegments
              ? html`<omni-loading-indicator>
                  <p>Loading Custom Segments&hellip;</p>
                </omni-loading-indicator>`
              : repeat(
                  this.customSegments,
                  segment => segment.uuid,
                  (segment, idx) => this.#renderSegmentCard(segment, idx),
                )}
          </div>
        </omni-tile>
      </omni-style>
    `;
  }
}

OmniElement.register(
  'campaign-audience-add-segments',
  CampaignAudienceAddSegments,
);
